<template>
  <div class="page-container">
    <div class="page-title-container">
      <a v-if="payroll && $route.params.id" href="#" class="text-primary d-inline-block mb-2"
         @click.prevent="$router.push({ name: 'payroll-engine-list' })">
        ← Planillas
      </a>
      <h1 class="page-title">{{ pageTitle }}</h1>
    </div>

    <!-- Crear período (solo si no hay una planilla cargada) -->
    <wz-card class="mb-4" v-if="!payroll">
      <template #header>
        <div class="wz-card-title">Crear periodo</div>
      </template>
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <div class="mb-3">
              <label>Empresa *</label>
              <wz-select id="companyId" v-model="form.companyId" :items="companies" defaultOption="Elegir empresa" />
            </div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="mb-3">
              <label>Tipo de planilla *</label>
              <wz-select id="payrollTypeId" v-model="form.payrollTypeId" :items="payrollTypes" defaultOption="Elegir tipo" />
              <!-- Solo para tipos con modo de cálculo BY_INSERTION (ingreso manual de datos) -->
              <wz-button v-if="isByInsertion" variant="outline" size="sm" class="mt-2" @click="showMovementModal = true">
                <i class="ri-upload-2-line mr-1"></i>Cargar movimientos
              </wz-button>
            </div>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <div class="mb-3">
              <wz-datepicker label="Fecha de inicio *" v-model="form.periodStart" />
            </div>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <div class="mb-3">
              <wz-datepicker label="Fecha de fin *" v-model="form.periodEnd" />
            </div>
          </div>
          <div class="w-full md:w-1/6 flex items-end px-2">
            <div class="mb-3 w-full px-2">
              <wz-button variant="primary" block class="px-2" @click="createPeriod"
                :disabled="!canCreate || creating">
                {{ creating ? 'Creando...' : 'Crear periodo' }}
              </wz-button>
            </div>
          </div>
        </div>
        <div v-if="form.payrollTypeId && form.periodStart && form.periodEnd" class="px-2 pb-2">
          <wz-alert v-if="durationError" variant="danger" icon="ri-error-warning-line">{{ durationError }}</wz-alert>
          <wz-alert v-else-if="durationInfo" variant="info" icon="ri-information-line">{{ durationInfo }}</wz-alert>
        </div>
    </wz-card>

    <!-- Planilla creada -->
    <wz-card v-if="payroll" class="mb-4">
      <template #header-title>Planilla #{{ payroll.id }}</template>
      <template #header-actions>
        <wz-badge :variant="statusBadge">{{ payroll.payrollStatus }}</wz-badge>
      </template>
        <div class="flex flex-wrap mb-3 -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <small class="text-muted block">Tipo</small>
            <strong>{{ payroll.payrollTypeName }}</strong>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <small class="text-muted block">Empresa</small>
            <strong>{{ payroll.companyName }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">Periodo</small>
            <strong>{{ payroll.periodYear }}-{{ String(payroll.periodMonth).padStart(2, '0') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2">
            <small class="text-muted block">Rango</small>
            <strong>{{ periodRange || '—' }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">Colaboradores</small>
            <strong>{{ payroll.totalEmployees }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">Estado</small>
            <strong>{{ payroll.payrollStatus }}</strong>
          </div>
        </div>

        <!-- Totales (solo si calculado) -->
        <div class="flex flex-wrap mb-3 -mx-2" v-if="payroll.payrollStatus !== 'DRAFT'">
          <div class="w-full md:w-1/4 px-2">
            <small class="text-muted block">Total bruto</small>
            <strong class="text-success">S/ {{ formatNumber(payroll.totalGross) }}</strong>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <small class="text-muted block">Total neto</small>
            <strong class="text-primary">S/ {{ formatNumber(payroll.totalNet) }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">Aportes (AFP/ONP/EsSalud)</small>
            <strong class="text-danger">S/ {{ formatNumber(payroll.totalContributions) }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">IR 5ta categoria</small>
            <strong class="text-warning">S/ {{ formatNumber(payroll.totalIr) }}</strong>
          </div>
          <div class="w-full md:w-1/6 px-2">
            <small class="text-muted block">Calculado por</small>
            <strong>{{ payroll.calculatedBy || '---' }}</strong>
          </div>
        </div>

        <div class="flex">
          <wz-button variant="primary" class="mr-2" @click="calculate"
            v-if="payroll.payrollStatus === 'DRAFT' || payroll.payrollStatus === 'CALCULATED'"
            :disabled="calculating">
            {{ calculating ? 'Calculando...' : (payroll.payrollStatus === 'CALCULATED' ? 'Recalcular' : 'Calcular') }}
          </wz-button>
          <wz-button variant="primary" class="mr-2" @click="approve"
            v-if="payroll.payrollStatus === 'CALCULATED'"
            :disabled="hasNegativeNet">
            Aprobar
          </wz-button>
          <wz-button variant="primary" class="mr-2" @click="markAsPaid"
            v-if="payroll.payrollStatus === 'APPROVED'">
            Marcar como pagado
          </wz-button>
          <wz-button variant="light" class="mr-2" @click="annul"
            v-if="payroll.payrollStatus !== 'ANNULLED'">
            Anular
          </wz-button>
          <wz-button variant="light" @click="exportExcel"
            v-if="payroll.payrollStatus !== 'DRAFT'"
            :disabled="exporting">
            <i class="ri-file-excel-line mr-1"></i>{{ exporting ? 'Exportando...' : 'Exportar Excel' }}
          </wz-button>
          <wz-button variant="light" @click="downloadPayslipsZip"
            v-if="payroll.payrollStatus === 'PAID'"
            :disabled="downloadingPayslips">
            <i class="ri-file-pdf-2-line mr-1"></i>{{ downloadingPayslips ? 'Generando...' : 'Descargar boletas' }}
          </wz-button>
          <wz-button variant="light" @click="openPlamePreview"
            v-if="payroll.payrollStatus === 'APPROVED' || payroll.payrollStatus === 'PAID'"
            :disabled="plame.loading">
            <i class="ri-government-line mr-1"></i>{{ plame.loading ? 'Cargando...' : 'Exportar PLAME' }}
          </wz-button>
          <wz-button variant="light" @click="openPaymentFile"
            v-if="payroll.payrollStatus === 'APPROVED' || payroll.payrollStatus === 'PAID'"
            :disabled="paymentFile.loading">
            <i class="ri-bank-line mr-1"></i>{{ paymentFile.loading ? 'Cargando...' : 'Generar archivo de pago' }}
          </wz-button>
        </div>
        <div v-if="hasNegativeNet && payroll.payrollStatus === 'CALCULATED'" class="text-danger mt-2">
          <small>Hay netos negativos: no se puede aprobar.</small>
        </div>
    </wz-card>

    <!-- Advertencia: colaboradores con documentación incompleta (se incluyen y pagan igual) -->
    <wz-card v-if="payroll && payroll.excludedEmployees && payroll.excludedEmployees.length" class="mb-4" :body-padding="false">
      <template #header>
        <div class="wz-card-title text-warning">
          <i class="ri-error-warning-line mr-2"></i>
          Documentación incompleta — se incluyen en el pago ({{ payroll.excludedEmployees.length }})
        </div>
      </template>
        <wz-alert variant="warning" icon="ri-information-line" class="mx-3 mt-3 mb-0">
          Estos colaboradores trabajaron y se les paga igual. La documentación faltante es una advertencia
          (expone a la empresa a una multa, no habilita a retener el sueldo). Se pedirá confirmación al aprobar.
        </wz-alert>
        <div class="overflow-x-auto">
          <table class="wz-table w-full px-2">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>DNI</th>
                <th>Documentos faltantes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="exc in payroll.excludedEmployees" :key="exc.employeeId">
                <td class="pl-3">{{ exc.employeeName }}</td>
                <td>{{ exc.identifier }}</td>
                <td class="text-danger">{{ exc.missingDocuments ? exc.missingDocuments.join(', ') : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
    </wz-card>

    <!-- Alerta: netos negativos -->
    <wz-alert v-if="hasNegativeNet" variant="warning" icon="ri-error-warning-line" class="mb-4">
      <div v-for="d in negativeNetDetails" :key="d.id">
        <strong>ALERTA:</strong> {{ d.employeeName }} tiene neto negativo (S/ {{ formatNumber(d.netSalary) }}). Revise los descuentos antes de aprobar.
      </div>
    </wz-alert>

    <!-- Detalle por empleado -->
    <wz-card v-if="details.length > 0" :body-padding="false">
      <template #header>
        <div class="wz-card-title">Detalle por colaborador ({{ details.length }})</div>
      </template>
        <wz-alert variant="info" icon="ri-information-line" class="mx-3 mt-3 mb-2">
          Haga clic en el nombre del colaborador para ver el detalle del cálculo.
        </wz-alert>
        <div class="overflow-x-auto">
          <table class="wz-table w-full px-2">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Documento</th>
                <th class="text-right">Sueldo base</th>
                <th class="text-right">Asig. fam.</th>
                <th class="text-right">Bruto</th>
                <th>Pensión</th>
                <th class="text-right">AFP/ONP</th>
                <th class="text-right">IR 5ta</th>
                <th class="text-right">EsSalud</th>
                <th class="text-right">Neto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in details" :key="d.id">
                <td class="pl-3">
                  <a href="#" class="payroll-detail-link text-primary" title="Ver detalle del cálculo" @click.prevent="openDetail(d)">{{ d.employeeName }}</a>
                  <i v-if="d.hasMissingDocuments" class="ri-error-warning-line text-warning ml-1"
                     :title="'Documentación incompleta: ' + ((d.missingDocuments || []).join(', ') || 'faltan documentos obligatorios')"></i>
                </td>
                <td>{{ d.employeeDocumentNumber }}</td>
                <td class="text-right">{{ formatNumber(d.baseSalary) }}</td>
                <td class="text-right">{{ formatNumber(d.familyAllowance) }}</td>
                <td class="text-right font-bold">{{ formatNumber(d.grossSalary) }}</td>
                <td>
                  <wz-badge variant="secondary-solid">{{ d.pensionSystem || '-' }}</wz-badge>
                  <small v-if="d.pensionEntityName && d.pensionSystem === 'AFP'" class="ml-1">{{ d.pensionEntityName }}</small>
                </td>
                <td class="text-right text-danger">{{ formatNumber(d.totalAfp > 0 ? d.totalAfp : d.onpAmount) }}</td>
                <td class="text-right text-warning">{{ formatNumber(d.irRetention) }}</td>
                <td class="text-right text-muted">{{ formatNumber(d.essaludAmount) }}</td>
                <td class="text-right font-bold text-primary">{{ formatNumber(d.netSalary) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-bold" style="background: #f8f9fa;">
                <td class="pl-3" colspan="2">TOTALES</td>
                <td class="text-right">{{ formatNumber(sumField('baseSalary')) }}</td>
                <td class="text-right">{{ formatNumber(sumField('familyAllowance')) }}</td>
                <td class="text-right">{{ formatNumber(sumField('grossSalary')) }}</td>
                <td></td>
                <td class="text-right text-danger">{{ formatNumber(sumPension()) }}</td>
                <td class="text-right text-warning">{{ formatNumber(sumField('irRetention')) }}</td>
                <td class="text-right text-muted">{{ formatNumber(sumField('essaludAmount')) }}</td>
                <td class="text-right text-primary">{{ formatNumber(sumField('netSalary')) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
    </wz-card>

    <!-- Modal: detalle del cálculo por colaborador (boleta) -->
    <wz-modal v-model="showDetailModal" title="Detalle del cálculo" size="lg">
      <div v-if="selectedDetail">
        <!-- Aviso: el desglose de movimientos no cuadra con los totales guardados -->
        <wz-alert v-if="!loadingMovements && hasMovementInconsistency" variant="warning" icon="ri-error-warning-line" class="mb-3">
          Los descuentos y adicionales cambiaron después del último cálculo. Recalcule la planilla para actualizar los totales.
        </wz-alert>

        <!-- Encabezado -->
        <div class="mb-3 pb-2" style="border-bottom: 1px solid #e5e7eb;">
          <div class="font-bold text-lg">{{ selectedDetail.employeeName }}</div>
          <div class="text-muted text-sm">
            DNI {{ selectedDetail.employeeDocumentNumber }}
            <span v-if="selectedDetail.positionName"> · {{ selectedDetail.positionName }}</span>
            <span v-if="selectedDetail.areaName"> · {{ selectedDetail.areaName }}</span>
          </div>
          <div class="text-muted text-sm">Días trabajados: {{ selectedDetail.effectiveDays }} / {{ selectedDetail.periodDays }}</div>
        </div>

        <div v-if="loadingMovements" class="text-muted text-sm mb-2">Cargando desglose…</div>

        <!-- INGRESOS -->
        <h5 class="font-bold mb-2">Ingresos</h5>
        <div class="flex justify-between"><span>Sueldo básico</span><span>S/ {{ formatNumber(selectedDetail.baseSalary) }}</span></div>
        <div class="flex justify-between"><span>Asignación familiar</span><span>S/ {{ formatNumber(selectedDetail.familyAllowance) }}</span></div>
        <div class="flex justify-between"><span>Adicionales remunerativos</span><span>S/ {{ formatNumber(selectedDetail.remunerativeAdditions) }}</span></div>
        <div v-for="m in additionsRem" :key="'ar' + m.id" class="flex justify-between text-muted text-sm pl-3">
          <span>· {{ m.conceptName }} ({{ m.conceptCode }})</span><span>S/ {{ formatNumber(m.amount) }}</span>
        </div>
        <div class="flex justify-between font-bold mt-1 pt-1" style="border-top: 1px solid #e5e7eb;">
          <span>Bruto</span><span class="text-success">S/ {{ formatNumber(selectedDetail.grossSalary) }}</span>
        </div>

        <!-- DESCUENTOS -->
        <h5 class="font-bold mb-2 mt-3">Descuentos</h5>
        <template v-if="selectedDetail.pensionSystem === 'AFP'">
          <div class="flex justify-between"><span>AFP — Aporte</span><span>S/ {{ formatNumber(selectedDetail.afpContribution) }}</span></div>
          <div class="flex justify-between"><span>AFP — Prima de seguro</span><span>S/ {{ formatNumber(selectedDetail.afpInsurance) }}</span></div>
          <div class="flex justify-between"><span>AFP — Comisión</span><span>S/ {{ formatNumber(selectedDetail.afpCommission) }}</span></div>
          <div class="flex justify-between font-bold"><span>Total AFP</span><span>S/ {{ formatNumber(selectedDetail.totalAfp) }}</span></div>
        </template>
        <div v-else-if="selectedDetail.pensionSystem === 'ONP'" class="flex justify-between"><span>ONP</span><span>S/ {{ formatNumber(selectedDetail.onpAmount) }}</span></div>
        <!-- Impuesto a la renta (DEF-005: override manual con trazabilidad) -->
        <div class="flex justify-between items-center">
          <span>
            Impuesto a la renta
            <wz-badge
              v-if="selectedDetail.irOverride != null"
              variant="warning"
              class="ml-1"
              :title="irOverrideTooltip(selectedDetail)">
              Ajustado manualmente
            </wz-badge>
          </span>
          <span class="flex items-center">
            <template v-if="selectedDetail.irOverride != null">
              <span class="text-muted text-sm mr-2" style="text-decoration: line-through;">
                S/ {{ formatNumber(selectedDetail.irRetention) }}
              </span>
              <span class="font-bold">S/ {{ formatNumber(selectedDetail.irOverride) }}</span>
            </template>
            <template v-else>
              <span>S/ {{ formatNumber(selectedDetail.irRetention) }}</span>
            </template>
            <wz-button
              v-if="payroll && payroll.payrollStatus === 'CALCULATED' && !irAdjust.open"
              variant="link"
              size="sm"
              class="ml-2"
              title="Ajustar manualmente el IR"
              @click="startIrAdjust">
              <i class="ri-edit-line"></i> Ajustar
            </wz-button>
          </span>
        </div>
        <!-- Formulario inline de ajuste -->
        <div v-if="irAdjust.open" class="p-2 mt-1 mb-1" style="background: #fff8e1; border: 1px solid #ffe082; border-radius: 6px;">
          <div class="text-sm text-muted mb-2">
            Valor calculado: S/ {{ formatNumber(selectedDetail.irRetention) }}. Ingrese el monto a aplicar y el motivo.
          </div>
          <div class="mb-2">
            <wz-input
              v-model="irAdjust.amount"
              type="number"
              min="0"
              step="0.01"
              label="Monto IR a aplicar (S/)"
              placeholder="0.00" />
          </div>
          <div class="mb-2">
            <wz-textarea
              v-model="irAdjust.reason"
              label="Motivo del ajuste"
              :rows="2"
              placeholder="Mínimo 10 caracteres (ej. comisiones variables no proyectadas por el cálculo automático)" />
            <small :class="irReasonValid ? 'text-muted' : 'text-danger'">
              {{ (irAdjust.reason || '').trim().length }}/10 caracteres mínimos
            </small>
          </div>
          <div class="flex justify-end">
            <wz-button variant="light" size="sm" class="mr-2" :disabled="irAdjust.saving" @click="cancelIrAdjust">Cancelar</wz-button>
            <wz-button variant="primary" size="sm" :disabled="!irAdjustValid || irAdjust.saving" @click="submitIrAdjust">
              {{ irAdjust.saving ? 'Guardando…' : 'Confirmar ajuste' }}
            </wz-button>
          </div>
        </div>
        <div class="flex justify-between"><span>Descuento por faltas</span><span>S/ {{ formatNumber(selectedDetail.absenceDiscount) }}</span></div>
        <div class="flex justify-between"><span>Descuento por licencia sin goce</span><span>S/ {{ formatNumber(selectedDetail.unpaidLeaveDiscount) }}</span></div>
        <div class="flex justify-between"><span>Descuento por tardanzas</span><span>S/ {{ formatNumber(selectedDetail.tardinessDiscount) }}</span></div>
        <div class="flex justify-between"><span>Otros descuentos</span><span>S/ {{ formatNumber(selectedDetail.otherDiscounts) }}</span></div>
        <div v-for="m in discountMovements" :key="'dm' + m.id" class="flex justify-between text-muted text-sm pl-3">
          <span>· {{ m.conceptName }} ({{ m.conceptCode }})</span><span>S/ {{ formatNumber(m.amount) }}</span>
        </div>
        <div class="flex justify-between font-bold mt-1 pt-1" style="border-top: 1px solid #e5e7eb;">
          <span>Total descuentos <small class="text-muted">(incluye AFP/ONP e IR)</small></span>
          <span class="text-danger">S/ {{ formatNumber(totalDeductions) }}</span>
        </div>

        <!-- Adicionales no remunerativos (suman al neto) -->
        <template v-if="Number(selectedDetail.nonRemunerativeAdditions) > 0 || additionsNonRem.length">
          <h5 class="font-bold mb-2 mt-3">Adicionales no remunerativos <small class="text-muted">(suman al neto)</small></h5>
          <div class="flex justify-between"><span>Total</span><span>S/ {{ formatNumber(selectedDetail.nonRemunerativeAdditions) }}</span></div>
          <div v-for="m in additionsNonRem" :key="'an' + m.id" class="flex justify-between text-muted text-sm pl-3">
            <span>· {{ m.conceptName }} ({{ m.conceptCode }})</span><span>S/ {{ formatNumber(m.amount) }}</span>
          </div>
        </template>

        <!-- TOTALES -->
        <div class="mt-3 pt-2" style="border-top: 2px solid #e5e7eb;">
          <div class="flex justify-between"><span>Bruto</span><span>S/ {{ formatNumber(selectedDetail.grossSalary) }}</span></div>
          <div v-if="Number(selectedDetail.nonRemunerativeAdditions) > 0" class="flex justify-between">
            <span>Adicionales no remunerativos</span><span>+ S/ {{ formatNumber(selectedDetail.nonRemunerativeAdditions) }}</span>
          </div>
          <div class="flex justify-between"><span>Total descuentos</span><span>− S/ {{ formatNumber(totalDeductions) }}</span></div>
          <div class="flex justify-between font-bold text-lg mt-1">
            <span>NETO</span><span class="text-primary">S/ {{ formatNumber(selectedDetail.netSalary) }}</span>
          </div>
        </div>

        <!-- REFERENCIA -->
        <div class="mt-3 pt-2 text-muted text-sm" style="border-top: 1px dashed #e5e7eb;">
          <div class="flex justify-between">
            <span>EsSalud (aporte del empleador — no afecta el neto)</span>
            <span>S/ {{ formatNumber(selectedDetail.essaludAmount) }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <wz-button
          v-if="payroll && payroll.payrollStatus === 'PAID' && selectedDetail"
          variant="primary"
          class="mr-2"
          :disabled="downloadingPayslip"
          @click="downloadPayslip">
          <i class="ri-file-pdf-2-line mr-1"></i>{{ downloadingPayslip ? 'Generando...' : 'Descargar boleta' }}
        </wz-button>
        <wz-button variant="light" @click="showDetailModal = false">Cerrar</wz-button>
      </template>
    </wz-modal>

    <!-- DEF-019: preview + validación pre-export de PLAME (el archivo final del PDT está pendiente de spec SUNAT) -->
    <wz-modal v-model="plame.show" title="Exportar PLAME — vista previa" size="lg">
      <div v-if="plame.data">
        <wz-alert variant="info" icon="ri-information-line" class="mb-3">
          Vista previa de los datos que irán al PDT PLAME (período {{ plame.data.periodMonth }}/{{ plame.data.periodYear }}).
          La generación del archivo del PDT se habilitará cuando se cargue el formato oficial de SUNAT.
        </wz-alert>

        <wz-alert v-for="(w, i) in plame.data.warnings" :key="'pw'+i" variant="warning" icon="ri-error-warning-line" class="mb-2">
          {{ w }}
        </wz-alert>

        <div class="overflow-x-auto">
          <table class="wz-table w-full">
            <thead>
              <tr>
                <th>Colaborador</th><th>Doc.</th><th>Régimen</th>
                <th class="text-right">Días</th><th class="text-right">Remuneración</th>
                <th class="text-right">Aporte</th><th class="text-right">IR 5ta</th><th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in plame.data.workers" :key="w.employeeId" :class="{ 'text-danger': w.warnings && w.warnings.length }">
                <td>{{ w.fullName }}</td>
                <td>{{ w.documentNumber || '—' }}</td>
                <td>{{ w.pensionSystem || '—' }}<span v-if="w.pensionEntityName"> · {{ w.pensionEntityName }}</span></td>
                <td class="text-right">{{ w.diasLaborados }}/{{ w.diasPeriodo }}</td>
                <td class="text-right">S/ {{ formatNumber(w.remuneracion) }}</td>
                <td class="text-right">S/ {{ formatNumber(w.aporteObligatorio) }}</td>
                <td class="text-right">S/ {{ formatNumber(w.ir5ta) }}</td>
                <td>
                  <span v-if="!w.warnings || !w.warnings.length" class="text-success">OK</span>
                  <ul v-else class="mb-0 pl-3 text-sm"><li v-for="(e, j) in w.warnings" :key="j">{{ e }}</li></ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <wz-button variant="light" @click="plame.show = false">Cerrar</wz-button>
        <wz-button variant="primary" :disabled="!plame.data || !plame.data.canExport || plame.downloading"
          :title="plame.data && !plame.data.canExport ? 'Corrija las observaciones / no hay colaboradores' : 'Descarga el ZIP con los 4 archivos del PDT PLAME'"
          @click="downloadPlame">
          <i class="ri-download-2-line mr-1"></i>{{ plame.downloading ? 'Generando...' : 'Descargar archivos PLAME' }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- DEF-020: archivo de pago masivo bancario (el formato por banco está pendiente de spec) -->
    <wz-modal v-model="paymentFile.show" title="Generar archivo de pago" size="lg">
      <div v-if="paymentFile.data">
        <div class="mb-3" style="max-width: 360px;">
          <label>Banco</label>
          <wz-select :items="paymentFile.bankOptions" v-model="paymentFile.bank"
            :defaultOption="paymentFile.bankOptions.length ? 'Seleccionar banco' : 'Sin formatos disponibles aún'" />
        </div>
        <wz-alert v-if="!paymentFile.bankOptions.length" variant="info" icon="ri-information-line" class="mb-3">
          Todavía no hay formatos de banco cargados. La generación del archivo se habilitará al cargar el
          formato oficial del banco de la empresa. Mientras tanto puede verificar los datos abajo.
        </wz-alert>
        <wz-alert v-for="(w, i) in paymentFile.data.warnings" :key="'pfw'+i" variant="warning" icon="ri-error-warning-line" class="mb-2">
          {{ w }}
        </wz-alert>
        <div class="mb-2 text-sm text-muted">
          A pagar: <strong>{{ paymentFile.data.payableCount }}</strong> colaborador(es) ·
          Total: <strong>S/ {{ formatNumber(paymentFile.data.totalAmount) }}</strong>
          <span v-if="paymentFile.data.incompleteCount"> · {{ paymentFile.data.incompleteCount }} incompleto(s)</span>
        </div>
        <div class="overflow-x-auto">
          <table class="wz-table w-full">
            <thead>
              <tr><th>Colaborador</th><th>Banco</th><th>Cuenta / CCI</th><th class="text-right">Monto</th><th>Estado</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in paymentFile.data.rows" :key="r.employeeId" :class="{ 'text-danger': !r.payable }">
                <td>{{ r.fullName }}</td>
                <td>{{ r.bankName || '—' }}</td>
                <td>{{ r.accountNumber || r.cci || '—' }}</td>
                <td class="text-right">S/ {{ formatNumber(r.amount) }}</td>
                <td>
                  <span v-if="r.payable" class="text-success">OK</span>
                  <ul v-else class="mb-0 pl-3 text-sm"><li v-for="(e, j) in r.warnings" :key="j">{{ e }}</li></ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <wz-button variant="light" @click="paymentFile.show = false">Cerrar</wz-button>
        <wz-button variant="primary" :disabled="!paymentFile.bank || !paymentFile.bankOptions.length"
          title="Se habilita al cargar el formato oficial del banco">
          Descargar archivo (próximamente)
        </wz-button>
      </template>
    </wz-modal>

    <!-- Confirmación de transiciones críticas (aprobar / pagar / anular) -->
    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      :accept-variant="confirmModal.acceptVariant"
      @confirm="onConfirm"
      @cancel="closeConfirmModal"
    />

    <!-- Carga de movimientos para tipos BY_INSERTION (no cierra el form del Motor) -->
    <movement-bulk-upload-modal
      v-model="showMovementModal"
      :context-label="movementModalLabel"
    />

  </div>
</template>

<script>
import { Alert, ConfirmModalMixin } from "@wizepe/wizeui"
import fileDownload from 'js-file-download'
import * as moment from 'moment'
import { CompanyService, PayrollTypeService, PayrollEngineService } from '@/core/services'
import MovementBulkUploadModal from '../Components/MovementBulkUploadModal.vue'

export default {
  components: { MovementBulkUploadModal },
  mixins: [ConfirmModalMixin],

  data() {
    return {
      companies: [],
      payrollTypes: [],
      form: {
        companyId: null,
        payrollTypeId: null,
        periodStart: null,
        periodEnd: null,
      },
      payroll: null,
      details: [],
      creating: false,
      calculating: false,
      exporting: false,
      downloadingPayslip: false,
      downloadingPayslips: false,
      // DEF-019: preview PLAME
      plame: { show: false, loading: false, downloading: false, data: null },
      // DEF-020: archivo de pago masivo
      paymentFile: { show: false, loading: false, data: null, bank: null, bankOptions: [] },
      showDetailModal: false,
      selectedDetail: null,
      detailMovements: [],
      loadingMovements: false,
      showMovementModal: false,
      // DEF-005: ajuste manual del IR 5ta
      irAdjust: { open: false, amount: '', reason: '', saving: false },
    }
  },
  watch: {
    'form.payrollTypeId'() {
      this.recalcEndDate()
    },
    'form.periodStart'() {
      this.recalcEndDate()
    },
  },
  computed: {
    // DEF-005: validación del formulario de ajuste de IR
    irReasonValid() {
      return (this.irAdjust.reason || '').trim().length >= 10
    },
    irAdjustValid() {
      const amount = Number(this.irAdjust.amount)
      return this.irAdjust.amount !== '' && !isNaN(amount) && amount >= 0 && this.irReasonValid
    },
    // DEF-017 #3: IR efectivo (override manual si existe) y total de descuentos REAL (incluye AFP/ONP e IR),
    // para que "Bruto − Total descuentos + adicionales no remun = NETO" cuadre en el modal.
    effectiveIrDetail() {
      const d = this.selectedDetail
      if (!d) return 0
      return Number(d.irOverride != null ? d.irOverride : d.irRetention) || 0
    },
    totalDeductions() {
      const d = this.selectedDetail
      if (!d) return 0
      const pension = Number(d.totalAfp) > 0 ? Number(d.totalAfp) : (Number(d.onpAmount) || 0)
      return pension + this.effectiveIrDetail + (Number(d.totalDiscounts) || 0)
    },
    selectedFrequency() {
      if (!this.form.payrollTypeId) return null
      const pt = this.payrollTypes.find(t => t.key === this.form.payrollTypeId)
      return pt ? pt.frequency : null
    },
    // Tipo seleccionado en el form de creación (null si aún no se eligió).
    selectedType() {
      if (!this.form.payrollTypeId) return null
      return this.payrollTypes.find(t => t.key === this.form.payrollTypeId) || null
    },
    isByInsertion() {
      return this.selectedType?.calculationMode === 'BY_INSERTION'
    },
    // Mensaje contextual del modal de carga de movimientos.
    movementModalLabel() {
      const name = this.selectedType ? this.selectedType.label : ''
      let period
      if (this.form.periodStart && this.form.periodEnd) {
        period = `del período ${moment(this.form.periodStart).format('DD/MM/YYYY')} al ${moment(this.form.periodEnd).format('DD/MM/YYYY')}`
      } else {
        period = 'del período que estás creando'
      }
      return `Cargar movimientos para ${name} ${period}`
    },
    canCreate() {
      if (!this.form.companyId || !this.form.payrollTypeId) return false
      if (!this.form.periodStart || !this.form.periodEnd) return false
      if (this.durationError) return false
      return true
    },
    // Días del rango (inclusivo). null si falta algún dato o el rango es inválido.
    durationDays() {
      if (!this.form.periodStart || !this.form.periodEnd) return null
      const start = moment(this.form.periodStart)
      const end = moment(this.form.periodEnd)
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null
      return end.diff(start, 'days') + 1
    },
    // Alerta informativa (Opción B). Vacía si falta tipo/fechas.
    durationInfo() {
      const n = this.durationDays
      if (!n || !this.selectedFrequency) return ''
      if (this.selectedFrequency === 'BIWEEKLY') return `Rango de ${n} días — equivalente a una quincena (15 días)`
      if (this.selectedFrequency === 'WEEKLY') return `Rango de ${n} días — equivalente a una semana (7 días)`
      return `Rango de ${n} días — equivalente a un mes estándar`
    },
    // Error de validación de duración (mismas fórmulas que backend). Vacío si es válido.
    durationError() {
      if (!this.form.payrollTypeId || !this.form.periodStart || !this.form.periodEnd || !this.selectedFrequency) return ''
      const start = moment(this.form.periodStart)
      const end = moment(this.form.periodEnd)
      if (!start.isValid() || !end.isValid()) return ''
      if (end.isBefore(start)) return 'La fecha de fin no puede ser anterior a la de inicio'
      const expected = this.computeExpectedEnd(this.form.periodStart, this.selectedFrequency)
      if (!expected || !end.isSame(moment(expected), 'day')) {
        const label = { BIWEEKLY: 'una quincena (15 días)', WEEKLY: 'una semana (7 días)' }[this.selectedFrequency] || 'un mes calendario'
        return `El rango debe corresponder a ${label}. Fecha de fin esperada: ${expected}`
      }
      return ''
    },
    statusBadge() {
      const map = {
        DRAFT: 'secondary-solid',
        CALCULATED: 'info-solid',
        APPROVED: 'success-solid',
        PAID: 'success-solid',
        ANNULLED: 'danger-solid',
      }
      return map[this.payroll?.payrollStatus] || 'dark'
    },
    negativeNetDetails() {
      return this.details.filter(d => Number(d.netSalary) < 0)
    },
    hasNegativeNet() {
      return this.negativeNetDetails.length > 0
    },
    additionsRem() {
      return this.detailMovements.filter(m => m.conceptType === 'ADICIONAL' && m.conceptNature === 'REMUNERATIVO' && m.status !== 'CANCELADO')
    },
    additionsNonRem() {
      return this.detailMovements.filter(m => m.conceptType === 'ADICIONAL' && m.conceptNature === 'NO_REMUNERATIVO' && m.status !== 'CANCELADO')
    },
    discountMovements() {
      return this.detailMovements.filter(m => m.conceptType === 'DESCUENTO' && m.status !== 'CANCELADO')
    },
    // Rango real de la planilla en formato peruano (DD/MM/YYYY al DD/MM/YYYY).
    periodRange() {
      if (!this.payroll || !this.payroll.periodStart || !this.payroll.periodEnd) return ''
      return `${moment(this.payroll.periodStart).format('DD/MM/YYYY')} al ${moment(this.payroll.periodEnd).format('DD/MM/YYYY')}`
    },
    pageTitle() {
      if (!this.$route.params.id) return 'Nueva planilla'
      if (this.payroll) {
        const label = this.periodRange || `${this.payroll.periodYear}-${String(this.payroll.periodMonth).padStart(2, '0')}`
        return `Detalle de planilla — ${this.payroll.payrollTypeName} ${label}`
      }
      return 'Detalle de planilla'
    },
    hasMovementInconsistency() {
      if (!this.selectedDetail) return false
      const sum = arr => arr.reduce((s, m) => s + (Number(m.amount) || 0), 0)
      const diff = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) > 0.001
      return diff(sum(this.discountMovements),  this.selectedDetail.otherDiscounts)
          || diff(sum(this.additionsRem),       this.selectedDetail.remunerativeAdditions)
          || diff(sum(this.additionsNonRem),    this.selectedDetail.nonRemunerativeAdditions)
    },
  },
  methods: {
    formatNumber(val) {
      if (val == null) return '0.00'
      return Number(val).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    sumField(field) {
      return this.details.reduce((sum, d) => sum + (Number(d[field]) || 0), 0)
    },
    sumPension() {
      return this.details.reduce((sum, d) => sum + (Number(d.totalAfp) || 0) + (Number(d.onpAmount) || 0), 0)
    },
    // DEF-005: ajuste manual de IR 5ta
    irOverrideTooltip(d) {
      if (!d || d.irOverride == null) return ''
      const who = d.irOverrideBy || 'usuario'
      const when = d.irOverrideAt ? moment(d.irOverrideAt).format('DD/MM/YYYY HH:mm') : ''
      return `Motivo: ${d.irOverrideReason || '—'}\nAjustado por ${who}${when ? ' el ' + when : ''}`
    },
    startIrAdjust() {
      this.irAdjust = {
        open: true,
        // precargar con el valor aplicado actual (override si existe, si no el calculado)
        amount: String(this.selectedDetail.irOverride != null
          ? this.selectedDetail.irOverride
          : this.selectedDetail.irRetention),
        reason: this.selectedDetail.irOverrideReason || '',
        saving: false,
      }
    },
    cancelIrAdjust() {
      this.irAdjust = { open: false, amount: '', reason: '', saving: false }
    },
    async submitIrAdjust() {
      if (!this.irAdjustValid) return
      this.irAdjust.saving = true
      try {
        const resp = await PayrollEngineService.overrideIr(
          this.selectedDetail.id, Number(this.irAdjust.amount), this.irAdjust.reason.trim())
        const updated = resp.payload
        // Actualizar el detalle abierto y la fila de la tabla.
        this.selectedDetail = { ...this.selectedDetail, ...updated }
        const idx = this.details.findIndex(x => x.id === updated.id)
        if (idx !== -1) this.$set(this.details, idx, { ...this.details[idx], ...updated })
        // Refrescar la cabecera (totales de la planilla cambian con el override).
        await this.loadPayrollHeader()
        this.cancelIrAdjust()
        Alert.success('IR ajustado manualmente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al ajustar el IR')
      } finally {
        this.irAdjust.saving = false
      }
    },
    async loadPayrollHeader() {
      try {
        const resp = await PayrollEngineService.get(this.payroll.id)
        if (resp.payload) this.payroll = resp.payload
      } catch (ex) {
        console.error(ex)
      }
    },
    async openDetail(d) {
      this.selectedDetail = d
      this.detailMovements = []
      this.cancelIrAdjust()
      this.showDetailModal = true
      this.loadingMovements = true
      try {
        const resp = await PayrollEngineService.getMovementsByEmployeePeriod(
          d.employeeId, this.payroll.periodYear, this.payroll.periodMonth)
        this.detailMovements = resp.payload || []
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al cargar el desglose del colaborador')
      } finally {
        this.loadingMovements = false
      }
    },
    // Fecha de fin esperada según el tipo (mismas fórmulas que backend), en ISO YYYY-MM-DD.
    computeExpectedEnd(start, frequency) {
      const s = moment(start)
      if (!s.isValid()) return null
      if (frequency === 'BIWEEKLY') return s.add(14, 'days').format('YYYY-MM-DD')
      if (frequency === 'WEEKLY') return s.add(6, 'days').format('YYYY-MM-DD')
      return s.add(1, 'months').subtract(1, 'days').format('YYYY-MM-DD') // MONTHLY (largo variable)
    },
    // Autocompleta la fecha de fin desde el inicio y el tipo. Se dispara al cambiar tipo o inicio.
    recalcEndDate() {
      if (!this.form.periodStart || !this.selectedFrequency) return
      this.form.periodEnd = this.computeExpectedEnd(this.form.periodStart, this.selectedFrequency)
    },
    async createPeriod() {
      this.creating = true
      try {
        const payload = {
          companyId: this.form.companyId,
          payrollTypeId: this.form.payrollTypeId,
          periodStart: moment(this.form.periodStart).format('YYYY-MM-DD'),
          periodEnd: moment(this.form.periodEnd).format('YYYY-MM-DD'),
        }
        const resp = await PayrollEngineService.createPeriod(payload)
        this.payroll = resp.payload
        this.details = []
        Alert.success(`Periodo creado con ${this.payroll.totalEmployees} colaboradores`)
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al crear periodo')
      } finally {
        this.creating = false
      }
    },
    async calculate(overrideConfirmed = false) {
      this.calculating = true
      try {
        const action = this.payroll.payrollStatus === 'CALCULATED' ? 'recalculate' : 'calculate'
        const resp = action === 'recalculate'
          ? await PayrollEngineService.recalculate(this.payroll.id)
          : await PayrollEngineService.calculate(this.payroll.id, overrideConfirmed)
        this.payroll = resp.payload
        await this.loadDetails()
        Alert.success('Planilla calculada correctamente')
      } catch (ex) {
        const msg = ex.errors ? ex.errors.message : ''
        // Guard 3 (DEF-013): días sin registro de asistencia → salida controlada. Se pide confirmación
        // explícita y se reintenta tratando esos días como asistidos.
        if (!overrideConfirmed && msg && msg.includes('sin registro de asistencia')) {
          this.calculating = false
          if (window.confirm(msg + '\n\n¿Confirma que revisó estos días sin registro y todos corresponden a '
              + 'asistencia normal? Se tratarán como asistidos.')) {
            return this.calculate(true)
          }
          return
        }
        console.error(ex)
        Alert.error(msg || 'Error al calcular')
      } finally {
        this.calculating = false
      }
    },
    // Las 3 acciones críticas abren una confirmación; la lógica real vive en do*().
    approve() {
      const missing = (this.payroll.excludedEmployees && this.payroll.excludedEmployees.length) || 0
      const base = 'Una vez aprobada, no podrá recalcularse. Los descuentos y adicionales pasarán a estado PROCESADO.'
      const warn = missing
        ? ` ATENCIÓN: ${missing} colaborador(es) tienen documentación incompleta y se aprobarán/pagarán de todos modos.`
        : ''
      this.openConfirmModal({
        title: '¿Aprobar planilla?',
        description: base + warn + ' ¿Está seguro?',
        acceptText: 'Sí, aprobar', cancelText: 'Cancelar',
        acceptVariant: missing ? 'warning' : 'primary', action: 'approve',
      })
    },
    markAsPaid() {
      this.openConfirmModal({
        title: '¿Marcar como pagada?',
        description: 'Se registrará que el pago fue ejecutado. ¿Está seguro?',
        acceptText: 'Sí, marcar como pagada', cancelText: 'Cancelar', acceptVariant: 'primary', action: 'pay',
      })
    },
    annul() {
      this.openConfirmModal({
        title: '¿Anular planilla?',
        description: 'La planilla quedará como ANULADA y los descuentos/adicionales volverán a estado PENDIENTE para ser aplicados en otra planilla. ¿Está seguro?',
        acceptText: 'Sí, anular', cancelText: 'Cancelar', acceptVariant: 'danger', action: 'annul',
      })
    },
    onConfirm() {
      const a = this.confirmModal.action
      this.closeConfirmModal()
      if (a === 'approve') this.doApprove()
      else if (a === 'pay') this.doPay()
      else if (a === 'annul') this.doAnnul()
    },
    async doApprove() {
      try {
        // La confirmación del modal cubre la advertencia documental: si hay no-compliant, confirmamos.
        const hasMissingDocs = !!(this.payroll.excludedEmployees && this.payroll.excludedEmployees.length)
        const resp = await PayrollEngineService.approve(this.payroll.id, hasMissingDocs)
        this.payroll = resp.payload
        Alert.success('Planilla aprobada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al aprobar')
      }
    },
    async doPay() {
      try {
        const today = new Date().toISOString().substring(0, 10)
        const resp = await PayrollEngineService.pay(this.payroll.id, today)
        this.payroll = resp.payload
        Alert.success('Planilla marcada como pagada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al registrar pago')
      }
    },
    async doAnnul() {
      try {
        const resp = await PayrollEngineService.annul(this.payroll.id)
        this.payroll = resp.payload
        this.details = []
        Alert.success('Planilla anulada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al anular')
      }
    },
    async loadDetails() {
      try {
        const resp = await PayrollEngineService.getDetails(this.payroll.id)
        this.details = resp.payload || []
      } catch (ex) {
        console.error(ex)
      }
    },
    async exportExcel() {
      this.exporting = true
      try {
        const resp = await PayrollEngineService.exportExcel(this.payroll.id)
        const fileName = `planilla_${this.payroll.payrollTypeCode || 'PL'}_${this.payroll.periodYear}-${String(this.payroll.periodMonth).padStart(2, '0')}.xlsx`
        fileDownload(resp.data || resp, fileName)
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al exportar Excel')
      } finally {
        this.exporting = false
      }
    },
    // DEF-018: boleta de pago del colaborador abierto en el modal (solo planilla PAGADA).
    async downloadPayslip() {
      if (!this.selectedDetail) return
      this.downloadingPayslip = true
      try {
        const resp = await PayrollEngineService.downloadPayslip(this.selectedDetail.id)
        const name = `boleta_${(this.selectedDetail.employeeName || 'colaborador').replace(/\s+/g, '_')}.pdf`
        fileDownload(resp.data || resp, name)
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al generar la boleta')
      } finally {
        this.downloadingPayslip = false
      }
    },
    // DEF-019: abre el preview/validación de PLAME (solo APROBADA/PAGADA).
    async openPlamePreview() {
      this.plame.loading = true
      try {
        const resp = await PayrollEngineService.plamePreview(this.payroll.id)
        this.plame.data = resp.payload
        this.plame.show = true
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al generar la vista previa de PLAME')
      } finally {
        this.plame.loading = false
      }
    },
    // DEF-020: abre el preview del archivo de pago masivo (solo APROBADA/PAGADA).
    async openPaymentFile() {
      this.paymentFile.loading = true
      try {
        const [prev, banks] = await Promise.all([
          PayrollEngineService.paymentFilePreview(this.payroll.id),
          PayrollEngineService.paymentFileBanks(this.payroll.id),
        ])
        this.paymentFile.data = prev.payload
        this.paymentFile.bankOptions = (banks.payload || []).map(b => ({ key: b.code, label: b.label }))
        this.paymentFile.bank = this.paymentFile.bankOptions.length ? this.paymentFile.bankOptions[0].key : null
        this.paymentFile.show = true
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al preparar el archivo de pago')
      } finally {
        this.paymentFile.loading = false
      }
    },
    // DEF-019: descarga el ZIP con los 4 archivos del PDT PLAME.
    async downloadPlame() {
      this.plame.downloading = true
      try {
        const resp = await PayrollEngineService.plameExport(this.payroll.id)
        const name = `plame_${this.payroll.periodYear}-${String(this.payroll.periodMonth).padStart(2, '0')}.zip`
        fileDownload(resp.data || resp, name)
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al generar los archivos PLAME')
      } finally {
        this.plame.downloading = false
      }
    },
    // DEF-018: ZIP con todas las boletas de la planilla (solo PAGADA).
    async downloadPayslipsZip() {
      this.downloadingPayslips = true
      try {
        const resp = await PayrollEngineService.downloadPayslipsZip(this.payroll.id)
        const name = `boletas_${this.payroll.payrollTypeCode || 'PL'}_${this.payroll.periodYear}-${String(this.payroll.periodMonth).padStart(2, '0')}.zip`
        fileDownload(resp.data || resp, name)
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al generar las boletas')
      } finally {
        this.downloadingPayslips = false
      }
    },
  },
  async created() {
    try {
      const [companiesResp, typesResp] = await Promise.all([
        CompanyService.listAll(),
        PayrollTypeService.listActive(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.payrollTypes = (typesResp.payload || []).map(t => ({ key: t.id, label: t.name, frequency: t.frequency, calculationMode: t.calculationMode }))
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar catalogos')
    }

    // Reapertura por ID: /planillas/motor/:id → cargar la planilla existente en el workspace.
    const id = this.$route.params.id
    if (id) {
      try {
        const resp = await PayrollEngineService.get(id)
        this.payroll = resp.payload
        await this.loadDetails()   // clave para el :disabled del neto negativo
      } catch (ex) {
        console.error(ex)
        Alert.error('No se pudo cargar la planilla')
        this.$router.push({ name: 'payroll-engine' })
      }
    }
  },
}
</script>

<style scoped>
/* Indicación visual de que el nombre del colaborador abre el detalle del cálculo. */
.payroll-detail-link {
  cursor: pointer;
  text-decoration: none;
}
.payroll-detail-link:hover {
  text-decoration: underline;
}
</style>
